import { ReactElement, useCallback, useMemo } from 'react'
import { Box3 } from 'three'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { Button, Stack, Typography } from '@mui/material'
import type { AlignmentConfig } from '@editor/components/Controls/IFCUploadWidget/types/ifcMetadata'
import {
  getGuidToHiddenFromTypes,
  getGuidToMGroup,
} from '@editor/components/Controls/IFCUploadWidget/utils/ifcMetadataUtils'
import {
  getAlignmentMatrix,
  getGuidToAlignedBox,
} from '@editor/pages/IFCImport/utils/glbElementGeometry'
import { useSnackbar } from '@ui/snackbar'
import { useControlStore, useSceneStore } from '@editorStores'
import { useIfcFiles } from '@editorHooks'
import { getSceneBoundingBoxFromGltfModel } from '../CameraOriginSelect/utils'

interface Props {
  /** Saves the automatically derived alignment config on the active file. */
  onAlign: (config: AlignmentConfig) => void
  /** Resets the active file's alignment config to the default (zero) alignment. */
  onReset: () => void
}

/**
 * "Automatisch" tab of the alignment section: derives the alignment from the model geometry by
 * translating the minimum corner of the bounding box of the *visible* elements to the origin (no
 * rotation). Filtered and hidden elements don't count, so irrelevant geometry (terrain, furniture)
 * can be excluded by hiding it first. Models without element metadata fall back to the full box.
 */
const AutoAlignmentTab = ({ onAlign, onReset }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const activeIfcFileGuid = useControlStore(state => state.activeIfcFileGuid)
  const hiddenElementIds = useControlStore(state => state.hiddenElementIds)
  const glbTypeVisibility = useControlStore(state => state.glbTypeVisibility)
  const loadedGltfModels = useSceneStore(state => state.loadedGltfModels)
  const model = activeIfcFileGuid ? loadedGltfModels[activeIfcFileGuid] : undefined

  const glbFiles = useIfcFiles()
  const metadata = useMemo(() => {
    const activeFile = activeIfcFileGuid
      ? glbFiles.find((file: FileItem) => file.guid === activeIfcFileGuid) ?? null
      : null
    return activeFile?.additional_data?.model_metadata ?? null
  }, [activeIfcFileGuid, glbFiles])

  const handleAutoAlign = useCallback(() => {
    if (!model) return

    const guidToMGroup = getGuidToMGroup(metadata)
    const allGuids = Object.keys(guidToMGroup)

    let box: Box3 | null
    if (allGuids.length === 0) {
      // No element metadata (e.g. externally produced GLB): fall back to the full model box.
      box = getSceneBoundingBoxFromGltfModel(model)
    } else {
      const filteredGuids = new Set<string>((metadata?.filteredElements ?? []).flat())
      const hiddenFromTypes = getGuidToHiddenFromTypes(guidToMGroup, glbTypeVisibility)
      const visibleGuids = new Set(
        allGuids.filter(
          guid => !filteredGuids.has(guid) && !hiddenElementIds.has(guid) && !hiddenFromTypes[guid],
        ),
      )
      // Raw model space (only the Z-up rotation), matching the manual reference-point fields.
      const guidToBox = getGuidToAlignedBox(model, visibleGuids, getAlignmentMatrix(null, false))
      const union = Object.values(guidToBox).reduce((acc, b) => acc.union(b), new Box3())
      box = union.isEmpty() ? null : union
    }

    if (!box) {
      enqueueSnackbar(
        'Keine sichtbaren Elemente gefunden. Blenden Sie die relevanten Elemente ein.',
        { variant: 'info' },
      )
      return
    }

    onAlign({
      referencePointIFCCS: [box.min.x, box.min.y, box.min.z],
      referencePointModelCS: [0, 0, 0],
      zRotationDegrees: 0,
    })
  }, [model, metadata, glbTypeVisibility, hiddenElementIds, onAlign, enqueueSnackbar])

  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        Das Modell wird automatisch anhand der sichtbaren Elemente an den Ursprung verschoben.
        Blenden Sie vorher nicht relevante Elemente (z.&nbsp;B. Gelände) aus — ausgeblendete und
        gefilterte Elemente werden nicht berücksichtigt.
      </Typography>

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          size="small"
          startIcon={<AutoFixHighIcon />}
          onClick={handleAutoAlign}
          disabled={!model}
          data-cy="btn-ifc-auto-alignment"
        >
          Automatisch
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={onReset}
          data-cy="btn-ifc-auto-reset-alignment"
        >
          Zurücksetzen
        </Button>
      </Stack>
    </Stack>
  )
}

export default AutoAlignmentTab
